import { Grid, Link } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import ErrorIcon from '@material-ui/icons/Error';
import GetAppIcon from '@material-ui/icons/GetApp';
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { removeDoc, uploadDocument } from '../../../services/fenergo-service';
import {
  fetchDocumentUrl,
  fetchUploadedDocuments
} from "../../../stores/async/async-actions";
import { useEntityState, useSelectedEntity } from "../../../stores/entity/entity.hooks";
import { setSnackBar } from '../../../stores/ui/ui-action';
import Dialog from "../../portico/dialog/dialog";
import { LoadingOnBackdrop } from "../../portico/loading/loading";
import { AnimatedLoading } from "../../portico/animated-loading/animated-loading";
import { PopoverTooltip } from "../../portico/tooltip/tooltip";
import FileUploader from "../file-uploader";
import { setDocumentsLoadInProgress } from "../../../stores/entity/entity-action";
import useDocumentsRendererStyles from './documents-renderer.styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { DocumentMetaData } from "../../../interfaces/DocumentMetaData.interface";
import CONSTANTS from './../../../utils/constants';
import STATIC_TEXT from './../../../content/common.json';
import HelpIcon from '@material-ui/icons/Help';
import {Accept} from 'react-dropzone'

interface DocumentsRendererProps {
  dataSource: Array<Object>;
  journeyId: string;
  entityId: string;
  loadingDocuments: boolean;
  journeyDocuments: Array<Object>;
  showLabel?: boolean;
  metaData?: DocumentMetaData;
  reviewDocuments?: boolean
  hasError?: boolean;
  error?: any;
  register?: any;
  unregister?: any;
  setValue?: any;
  fieldName?: string;
  name?: string;
  documentMeta?: any;
  deleteRequired: boolean;
  setUploadCancel?: any;
  documentDescription?: string;
  onChange?: any;
}

export const DocumentsRenderer: React.FC<DocumentsRendererProps> = ({
  dataSource, journeyId, entityId, journeyDocuments, loadingDocuments,
  showLabel = true, metaData, reviewDocuments = false, documentMeta = {}, error, register, unregister, setValue, name, deleteRequired, setUploadCancel, documentDescription,
  onChange
}) => {
  const currentStepperIndex = useSelector((state: any) => state.ui.currentStepperIndex, () => true);
  const journeyTasks = useSelector((state: any) => state.entity.initialJourneyTasks);
  const currentTask = journeyTasks[currentStepperIndex]?.name;
  const selectedEntity = useSelectedEntity();
  const [removing, setRemoving] = useState<boolean>(false);
  const [removeModalVisible, setRemoveModalVisible] = useState<boolean>(false);
  const [documentToRemove, setDocumentToRemove] = useState<Array<object>>(null);
  const dispatch = useDispatch();

  const documentsRendererStyles = useDocumentsRendererStyles();

  const [documentIdToDownload, setDocumentIdToDownload] = useState<string>();
  const { documentDownloadUrls, documentDownloadUrlsLoadInProgress, documentsLoadInProgress } = useEntityState();

  // Used to show the loading spinner when the component is loading
  // we delete documents on load
  const [loading, setLoading] = useState<boolean>(false);

  const downloadDocument = async (documentId) => {
    setDocumentIdToDownload(documentId);
    // Always fetch the document url as url is active for very few secs.
    dispatch(fetchDocumentUrl(documentId));
  }

  const removeDocument = async () => {
    setRemoving(true);
    try {
      await removeDoc(documentToRemove["id"]);

      setRemoving(false);
      setDocumentToRemove(null);
      dispatch(fetchUploadedDocuments(journeyId, selectedEntity.id));

      setValue && setValue(name, '', { shouldValidate: true });
    } catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while deleting the document', description: error.message, messageType: 'error' }));
      setRemoving(false);
    }
    setRemoveModalVisible(false);
  }

  const cancelUpload = (uploadCancel) => {
    setUploadCancel(true);
  }

  const handleCancelModal = () => {
    setRemoveModalVisible(false);
  };

  useEffect(() => {
    if (!documentDownloadUrlsLoadInProgress && documentIdToDownload && documentDownloadUrls[documentIdToDownload]) {
      window.open(documentDownloadUrls[documentIdToDownload]);
      setDocumentIdToDownload(null);
    }
  }, [documentDownloadUrlsLoadInProgress, documentDownloadUrls, documentIdToDownload, setDocumentIdToDownload]);

  useEffect(() => {
    setLoading(true);
    // deleteDocuments();
  }, []);

  useEffect(() => {
    return () => {
      // On unmount call unregister,
      // unregister is a callback passed from the parent component
      if (unregister) {
        unregister();
      }
    }
  }, []);

  useEffect(() => {
    if (loading && !documentsLoadInProgress) {
      setLoading(false);
    }
  }, [loading, documentsLoadInProgress]);

  const filterDocuments = (documentDataKey?: string) => {
    const documents = [];
    if (journeyDocuments) {
      journeyDocuments.forEach((document) => {
        const documentDataKeys = document["documentDataKey"];
        const getFilteredDocuments = !documentDataKey || (documentDataKey && documentDataKeys && documentDataKeys.includes(documentDataKey));

        if (getFilteredDocuments) {
          documents.push(document);
        }
      })
    }
    return documents;
  }

  const renderDocumentReqsOrDocs = (docRequirementId: string, documentDataKey: string, index: number, categoryName: string) => {
    const documents = filterDocuments(documentDataKey);
    const fileSize = metaData?.maxFileSize ? parseInt((metaData.maxFileSize).match(/\d+/g)[0]) : null;
    const maxFiles = metaData?.maxFiles ? metaData?.maxFiles : 1;
    const multiple = metaData?.multiple ? metaData?.multiple : true;
    const readOnlyDocument = metaData && metaData?.readOnlyDocument ? metaData?.readOnlyDocument : false;

    if (documents?.length === 0) {
      setValue && setValue(name, '')
    }

    let errorObj = null;

    if (error) {
      errorObj = error[`file${index}`] || error
    }
    return (
      <>
        {(!documents || documents.length < maxFiles) &&
          <FileUploader
            index={index}
            upload={(formData, setProgress: Function, setHideDropzone: Function) => handleUpload(formData, docRequirementId, documentDataKey, setProgress, setHideDropzone)}
            accept={metaData?.acceptableFileFormats as unknown as Accept}
            maxSizeInMB={fileSize}
            acceptableFileTypesNotes={metaData?.fileTypes}
            error={errorObj}
            cancelUpload={(uploadCancel) => cancelUpload(true)}
            multiple={multiple}
            maxFiles={maxFiles - documents.length}
            readOnlyDocument={readOnlyDocument}
            register={register}
            name={name}
            onChange={onChange}
            categoryName={categoryName}
          />
        }
        {renderDocuments(documents)}
      </>
    );
  }

  const handleUpload = async (formData, documentId, documentDataKey, setNewProgress: Function, setHideDropzone: Function) => {
    const file: File = formData.get('Data.File')
    const documentRequirementIds = [documentId];

    if (file?.size > 0) {

      const data = {
        fileName: file.name,
        entityId: selectedEntity?.id,
        journeyId,
        documentType: formData.get('Data.DocumentType'),
        friendlyName: formData.get('Data.FriendlyName').replace(/\.[^/.]+$/, ""),
        documentRequirementIds,
        documentDataKey,
      }

      let documentResponse

      try {
        setHideDropzone(true);
        dispatch(setDocumentsLoadInProgress(true));

        documentResponse = await uploadDocument(data, formData, setNewProgress);

        dispatch(fetchUploadedDocuments(journeyId, selectedEntity.id))
        // Set value
        setValue && setValue(name, documentResponse.data.id, { shouldValidate: true });

        //Show drop zone once file is uploaded
        setHideDropzone(false);

      } catch (e) {
        if (e.message === "canceled") {
          dispatch(setSnackBar({ show: true, message: 'Attempting to cancel request', messageType: 'info' }));
        } else {

          await removeDoc(documentResponse?.data?.id);
          dispatch(setSnackBar({ show: true, message: 'Error while calling API', description: e.message, messageType: 'error' }));

        }
        setHideDropzone(false);
      }
    }
    else {
      dispatch(setSnackBar({ show: true, message: 'Error uploading document', description: "Invalid file size : 0 bytes file uploaded", messageType: 'error' }));
    }
  }



  let documentNameSelectedToRemove = '';
  if (documentToRemove && documentToRemove['properties'] && documentToRemove['properties']['file'] && documentToRemove['properties']['file']['value']) {
    documentNameSelectedToRemove = documentToRemove['properties']['file']['value'];
  }

  const formatDate = (date) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      timeZoneName: "short"
    } as const;
    const dateObject = new Date(Date.parse(date));
    return dateObject.toLocaleString(undefined, options);
  }
  const renderDocuments = (journeyDocuments) => {

    const readOnlyDocument = metaData?.readOnlyDocument ? metaData?.readOnlyDocument : false;


    return journeyDocuments && journeyDocuments.map(document => {

      if (!(documentToRemove && documentToRemove["id"] && (document.id === documentToRemove["id"]))) {
        setValue && setValue(name, document.id, { shouldValidate: true });
      }
      const fileLabel = document.documentKey && document.documentKey.substring(document.documentKey.indexOf('_') + 1);
      const dateTime = document.properties?.dateTime?.value;

      return (
        <div key={document['id']} className={documentsRendererStyles.documentWrapper}>
          <div>
            <CheckCircleIcon fontSize="medium" className={documentsRendererStyles.fileStatus} />
          </div>
          <div>
            <div className={documentsRendererStyles.documentDetailsWrapper}>
              <div><span className={documentsRendererStyles.fileLabel}>{fileLabel}</span></div>
              <IconButton aria-label="download"
                color='primary'
                className={documentsRendererStyles.downloadBtn}
                data-testid="download-icon"
                onClick={async () => { await downloadDocument(document["id"]) }}>
                <GetAppIcon />
              </IconButton>
              {dateTime ? (
                <div style={{ color: "grey" }}>
                  (Uploaded: {formatDate(dateTime)})
                </div>
              ) : null}
            </div>
            {!readOnlyDocument &&
              (<Link data-testid={`delete-link-${document["id"]}`} underline="none" className={documentsRendererStyles.removeBtn}
                onClick={async () => { setDocumentToRemove(document); setRemoveModalVisible(true); }}>
                {CONSTANTS.DELETE}
              </Link>)
            }
          </div>
        </div>
      );
    })

  }

  return (
    <>
      <LoadingOnBackdrop status={documentDownloadUrlsLoadInProgress} message='Downloading the document...' />

      {loading && documentsLoadInProgress && <AnimatedLoading />}

      {!loading && (
        <form>
          <Grid container spacing={2}  >
            <Grid item xs={12} className={documentsRendererStyles.gridItem}>
              {!reviewDocuments && dataSource && dataSource.map((record, index) => {
                return (
                  <div key={record['id']}>
                    <div className={documentsRendererStyles.uploadHeaders}>
                      {record['name']}{record['description'] && <span className={documentsRendererStyles.endAdornmentWrapper}><PopoverTooltip messageDescription={record['description']}><HelpIcon /></PopoverTooltip></span>}
                    </div>
                      {record["documentDataKey"] === "otherDocuments" && (
                        <>
                          <div className={documentsRendererStyles.sectionDescription1}>
                            {CONSTANTS.DOCUMENT_POLICY.OTHER_DOC_TEXT}
                          </div>                          
                        </>
                      )}
                    <div key={record['id']} className={documentsRendererStyles.documentRequirementWrapper}>
                      {renderDocumentReqsOrDocs(record['id'], record['documentDataKey'], index, record['name'])}
                    </div>
                  </div>
                )
              })
              }

              {reviewDocuments && renderDocuments(filterDocuments())}

            {/* Note: This has the strange spread syntax for a type asseration. This allows us to
            modify the Dialog element props without change the source element's props, which
            come from the portico-ui-components library. We'll have to move all of these over to
            LendX's code base soon. */}
            <Dialog
              titleIcon={<ErrorIcon fontSize="large" className={documentsRendererStyles.deleteConfirmationTitleIcon} />}
              title={`${CONSTANTS.DELETE} ${documentNameSelectedToRemove} ?`}
              open={documentToRemove && removeModalVisible}
              cancelBtnLabel={CONSTANTS.CANCEL}
              onCancel={handleCancelModal}
              okBtnLabel={CONSTANTS.DELETE}
              okBtnClasses={documentsRendererStyles.deleteDialogBtn}
              onOk={removeDocument}
              disableActions={removing}
              {...{ children: <><LoadingOnBackdrop status={removing} message='Deleting the document...' />
              <div>{STATIC_TEXT.verifyIdentity.deleteDocumentMsg}</div></> } as any }
            />    
            </Grid>
          </Grid>
        </form>
      )}
    </>
  );
};